/**
 * App root for both redirect and popup modes.
 *
 * Shared:
 *   - Reads embed public_key from URL (?key=… in popup, ?via=… or
 *     route segment in redirect).
 *   - Bootstraps the EmbedClient API client.
 *   - Loads /embed/v1/config to know which events this snippet covers
 *     and what the organizer is called (for the attribution chrome).
 *   - Routes through the registration flow (Section 4a state machine
 *     in the design doc).
 *
 * Differs by mode:
 *   - Chrome wrapper. Redirect mode renders a full page with global
 *     header + footer; popup mode renders inside a borderless content
 *     box (the modal overlay is loader.js's responsibility, not ours).
 *   - Completion handoff. Popup posts back to host via postMessage;
 *     redirect renders a full summary page with the
 *     "Back to {organizer}" CTA when EmbedClient.return_url is set.
 *
 * For PR C (1/3), the routes are placeholders. Flow components fill
 * in across PRs C and D.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { EmbedClient, ConfigResponse } from '@/api/embed';
import { Chrome } from '@/components/Chrome';
import { Lockup } from '@/components/Lockup';
import { SignedIn } from '@/components/SignedIn';
import { featureOn } from '@/flags';
import { BackOverrideContext } from '@/state/backOverride';
import { FlowProvider } from '@/state/FlowContext';
import { ConfigProvider } from '@/state/vocab';
import { LandingPage } from '@/pages/LandingPage';
import { ChooseProductPage } from '@/pages/ChooseProductPage';
import { EventPage } from '@/pages/EventPage';
import { SponsorTierPage } from '@/pages/SponsorTierPage';
import { SponsorDetailsPage } from '@/pages/SponsorDetailsPage';
import { SponsorPaymentPage } from '@/pages/SponsorPaymentPage';
import { SponsorDonePage } from '@/pages/SponsorDonePage';
import { AuthGatePage } from '@/pages/AuthGatePage';
import { TeamCreatePage } from '@/pages/TeamCreatePage';
import { TeamPickerPage } from '@/pages/TeamPickerPage';
import { OfflinePayPage } from '@/pages/OfflinePayPage';
import { ConfirmPage } from '@/pages/ConfirmPage';
import { PaymentPage } from '@/pages/PaymentPage';
import { DonePage } from '@/pages/DonePage';
import { SponsorFulfillmentPage } from '@/pages/SponsorFulfillmentPage';
import { SponsorInvitePage } from '@/pages/SponsorInvitePage';
import { SponsorContactCardPage } from '@/pages/SponsorContactCardPage';
import { BulkEventsPage } from '@/pages/BulkEventsPage';
import { BulkDivisionsPage } from '@/pages/BulkDivisionsPage';
import { BulkReviewPage } from '@/pages/BulkReviewPage';
import { BulkPaymentPage } from '@/pages/BulkPaymentPage';
import { BulkDonePage } from '@/pages/BulkDonePage';
import { NotConfigured } from '@/pages/NotConfigured';
import * as Gfg from '@/gfg';

export type DisplayMode = 'redirect' | 'popup';

interface AppProps {
  displayMode: DisplayMode;
}

export function App({ displayMode }: AppProps) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Resolve the embed public_key. It arrives on the URL as ?key= (popup)
  // or ?via= (redirect) on first load — but internal navigate() calls
  // don't carry the query string forward, so once the user taps into
  // the flow the param is gone.
  //
  // THREE-LAYERED PERSISTENCE so navigation can't lose the key:
  //   1. URL  — wins on first load and any later visit that includes ?via=
  //   2. useRef — captures the value the FIRST time we see it on the URL
  //               and survives forever in this App instance; immune to
  //               sessionStorage being cleared, third-party cookie policy,
  //               or partitioned-storage quirks
  //   3. sessionStorage — survives a full page reload (the ref is wiped
  //               when the document is)
  //
  // We mirror writes to sessionStorage but treat the ref as the
  // authoritative in-memory cache for the life of this App.
  const keyRef = useRef<string | null>(null);

  const publicKey = useMemo(() => {
    const fromUrl = searchParams.get('key') ?? searchParams.get('via');
    if (fromUrl) {
      keyRef.current = fromUrl;
      try { sessionStorage.setItem('tn-embed-public-key', fromUrl); } catch { /* noop */ }
      return fromUrl;
    }
    if (keyRef.current) return keyRef.current;
    try {
      const fromStorage = sessionStorage.getItem('tn-embed-public-key');
      if (fromStorage) {
        keyRef.current = fromStorage;
        return fromStorage;
      }
    } catch { /* noop */ }
    return null;
  }, [searchParams]);

  const client = useMemo(
    () => (publicKey ? new EmbedClient({ publicKey }) : null),
    [publicKey],
  );

  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  // A page on an inner step (auth email/code) can take over the back control.
  const backOverride = useRef<(() => void) | null>(null);

  // The "next" generic look (styles/skin-next.css). Not for GFG, which
  // pins its own skin; organizer themes still win on every token they set.
  const nextSkin = !!config && featureOn(config, 'next_skin') && config.theme !== 'gfg';
  // Golf Fore Good runs its own registration screens (src/gfg); the sponsor
  // pages and bulk stay shared.
  const P = config?.theme === 'gfg' ? GFG_PAGES : TN_PAGES;
  useEffect(() => {
    document.documentElement.classList.toggle('tn-skin-next', nextSkin);
  }, [nextSkin]);

  useEffect(() => {
    if (!client) return;
    client
      .getConfig()
      .then(setConfig)
      .catch((err) => {
        setConfigError(
          err?.response?.status === 403
            ? 'This registration link is no longer active.'
            : 'Could not load registration. Please try again.',
        );
      });
  }, [client]);

  // Public, token-based sponsor asset upload page. Reached from the emailed
  // "Upload sponsor assets" link, which carries only the fulfillment_token —
  // no embed key — so it must render ahead of the key gate below.
  const fulfillmentMatch = matchPath('/sponsor-fulfillment/:token', location.pathname);
  if (fulfillmentMatch?.params.token) {
    return (
      <Chrome displayMode={displayMode} branding={null}>
        <div className="tn-page-frame">
          <SponsorFulfillmentPage token={fulfillmentMatch.params.token} />
        </div>
        <Lockup />
      </Chrome>
    );
  }

  // Public, token-based sponsor invite acceptance page. Reached from the
  // emailed "Accept sponsorship invite" link — invite_token only, no key.
  const inviteMatch = matchPath('/sponsor-invite/:token', location.pathname);
  if (inviteMatch?.params.token) {
    return (
      <Chrome displayMode={displayMode} branding={null}>
        <div className="tn-page-frame">
          <SponsorInvitePage token={inviteMatch.params.token} />
        </div>
        <Lockup />
      </Chrome>
    );
  }

  // Public, token-based Digital Contact Card purchase for an offline
  // sponsor. Reached from the organizer's emailed link — no embed key.
  const contactCardMatch = matchPath('/sponsor-contact-card/:token', location.pathname);
  if (contactCardMatch?.params.token) {
    return (
      <Chrome displayMode={displayMode} branding={null}>
        <div className="tn-page-frame">
          <SponsorContactCardPage token={contactCardMatch.params.token} />
        </div>
        <Lockup />
      </Chrome>
    );
  }

  if (!publicKey || !client) {
    return <NotConfigured />;
  }

  if (configError) {
    return (
      <Chrome displayMode={displayMode} branding={null}>
        <div role="alert" style={{ padding: 24, textAlign: 'center' }}>
          {configError}
        </div>
        <Lockup />
      </Chrome>
    );
  }

  // Header back button. The widget runs in a modal with no browser chrome,
  // so this is the only way back a step. Shown on the linear mid-flow steps
  // that don't manage their own back, and hidden on:
  //   - the first screen (landing / product chooser — nowhere to go)
  //   - auth (owns its email↔code sub-step back)
  //   - terminal steps (payment / offline / done / sponsor done)
  // Also requires actual in-flow history (idx > 0) so a deep-link entry
  // straight into a mid step doesn't show a back that would leave the widget.
  const BACK_STEPS = [
    '/events/:eventId',                 // register or sponsor
    '/events/:eventId/auth',            // sign in or guest
    '/events/:eventId/register',        // divisions
    '/events/:eventId/team-create',
    '/events/:eventId/teams',
    '/events/:eventId/confirm',
    '/events/:eventId/sponsor',
    '/events/:eventId/sponsor/:sponsorshipId',
    '/bulk/auth',
    '/bulk/teams',
    '/bulk/team-create',
    '/bulk/events',
    '/bulk/divisions',
    '/bulk/review',
    // Review reuses the batch it already created, so going back from the
    // bulk payment can't register twice. The single-event and sponsor
    // payments stay without one: Confirm would register again.
    '/bulk/payment',
  ];
  let historyIdx = 0;
  try { historyIdx = (window.history.state?.idx as number) ?? 0; } catch { /* noop */ }
  // Terminal steps never show back even if their path matches a param route
  // (e.g. /sponsor/done matches /sponsor/:sponsorshipId).
  const isTerminalStep =
    /\/(done|offline)$/.test(location.pathname) ||
    (/\/payment$/.test(location.pathname) && location.pathname !== '/bulk/payment');
  const showBack =
    historyIdx > 0 &&
    !isTerminalStep &&
    BACK_STEPS.some((p) => matchPath({ path: p, end: true }, location.pathname));

  return (
    <FlowProvider>
      <ConfigProvider config={config}>
      <BackOverrideContext.Provider value={backOverride}>
      <Chrome
        displayMode={displayMode}
        branding={config?.branding ?? null}
        theme={config?.theme}
        onBack={showBack ? () => (backOverride.current ? backOverride.current() : navigate(-1)) : undefined}
      >
        {/* `key={location.pathname}` forces the inner subtree to remount
            on every SPA navigation. The .tn-page-frame class's mount
            animation (defined in global.css) plays each time, giving
            the multi-step flow a single-surface feel even though it's
            structurally a series of route changes. */}
        <div key={location.pathname} className="tn-page-frame">
          <Routes>
            <Route path="/" element={<P.Landing client={client} config={config} />} />
            <Route path="/events/:eventId" element={<P.ChooseProduct client={client} config={config} />} />
            <Route path="/events/:eventId/register" element={<P.Event client={client} />} />
            <Route path="/events/:eventId/sponsor" element={<SponsorTierPage client={client} />} />
            <Route path="/events/:eventId/sponsor/done" element={<SponsorDonePage client={client} config={config} displayMode={displayMode} />} />
            <Route path="/events/:eventId/sponsor/:sponsorshipId" element={<SponsorDetailsPage client={client} />} />
            <Route path="/events/:eventId/sponsor/:sponsorshipId/payment" element={<SponsorPaymentPage client={client} />} />
            <Route path="/events/:eventId/auth" element={<P.AuthGate client={client} />} />
            <Route path="/events/:eventId/team-create" element={<P.TeamCreate client={client} />} />
            <Route path="/events/:eventId/teams" element={<P.TeamPicker client={client} />} />
            <Route path="/events/:eventId/confirm" element={<P.Confirm client={client} />} />
            <Route path="/events/:eventId/payment" element={<P.Payment client={client} />} />
            <Route
              path="/events/:eventId/offline"
              element={<P.OfflinePay config={config} displayMode={displayMode} />}
            />
            <Route
              path="/events/:eventId/done"
              element={<P.Done client={client} config={config} displayMode={displayMode} />}
            />
            {/* Bulk: one team, several events, one payment. The sign-in and
                team steps are the same pages as the single flow. */}
            <Route path="/bulk/auth" element={<AuthGatePage client={client} />} />
            <Route path="/bulk/teams" element={<TeamPickerPage client={client} />} />
            <Route path="/bulk/team-create" element={<TeamCreatePage client={client} />} />
            <Route path="/bulk/events" element={<BulkEventsPage client={client} />} />
            <Route path="/bulk/divisions" element={<BulkDivisionsPage client={client} />} />
            <Route path="/bulk/review" element={<BulkReviewPage client={client} />} />
            <Route path="/bulk/payment" element={<BulkPaymentPage client={client} />} />
            <Route path="/bulk/done" element={<BulkDonePage client={client} displayMode={displayMode} />} />
          </Routes>
        </div>
        {/* Golf Fore Good keeps its screens as they are (src/gfg). */}
        {config?.theme !== 'gfg' ? <SignedIn client={client} /> : null}
        <Lockup />
      </Chrome>
      </BackOverrideContext.Provider>
      </ConfigProvider>
    </FlowProvider>
  );
}

const TN_PAGES = {
  Landing: LandingPage,
  ChooseProduct: ChooseProductPage,
  Event: EventPage,
  AuthGate: AuthGatePage,
  TeamPicker: TeamPickerPage,
  TeamCreate: TeamCreatePage,
  Confirm: ConfirmPage,
  Payment: PaymentPage,
  OfflinePay: OfflinePayPage,
  Done: DonePage,
};

const GFG_PAGES: typeof TN_PAGES = {
  Landing: Gfg.GfgLandingPage,
  ChooseProduct: Gfg.GfgChooseProductPage,
  Event: Gfg.GfgEventPage,
  AuthGate: Gfg.GfgAuthGatePage,
  TeamPicker: Gfg.GfgTeamPickerPage,
  TeamCreate: Gfg.GfgTeamCreatePage,
  Confirm: Gfg.GfgConfirmPage,
  Payment: Gfg.GfgPaymentPage,
  OfflinePay: Gfg.GfgOfflinePayPage,
  Done: Gfg.GfgDonePage,
};
